import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';

import { CadsProgressBarConfig, CadsProgressBarVariant } from './cads-progress-bar.config';

let _cadsProgressBarTitleIdSeq = 0;

/**
 * Multi-style progress indicator: linear (default, segmented, utilization, mini) and circular (regular / large). Copy and
 * numeric formatting are supplied by the host via {@link CadsProgressBarConfig}.
 */
@Component({
    selector: 'cads-progress-bar',
    imports: [NgTemplateOutlet],
    templateUrl: './cads-progress-bar.component.html',
    styleUrl: './cads-progress-bar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.cads-progress-bar--animate]': '_animateProgress()',
    }
})
export class CadsProgressBarComponent {
  private readonly _elRef = inject(ElementRef<HTMLElement>);

  /**
   * Stable id for visible title text so `aria-labelledby` can reference the full string (mini truncation, circular
   * center, etc.).
   */
  protected readonly _ariaTitleDomId = `cads-pb-title-${++_cadsProgressBarTitleIdSeq}`;

  readonly config = input.required<CadsProgressBarConfig>();
  readonly variant = input<CadsProgressBarVariant>('default');

  /** Host class drives CSS transitions; default **true** when `animateProgress` is omitted. */
  protected readonly _animateProgress = computed(() => this.config().animateProgress !== false);

  private readonly _animateOnVisible = computed(() => this.config().animateOnVisible === true);

  private readonly _ioRootMargin = computed(() => this.config().animateOnVisibleRootMargin ?? '0px');

  private readonly _ioThreshold = computed(() => this.config().animateOnVisibleThreshold ?? 0.15);

  /** First intersection seen while `animateOnVisible` is **true**; reset when that flag turns on again. */
  private readonly _hasIntersected = signal(false);

  private _lastAnimateOnVisible = false;

  constructor() {
    effect((onCleanup) => {
      const on = this._animateOnVisible();
      if (on && !this._lastAnimateOnVisible) {
        this._hasIntersected.set(false);
      }
      this._lastAnimateOnVisible = on;

      if (!on) {
        return;
      }

      if (
        typeof globalThis.matchMedia === 'function' &&
        globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        this._hasIntersected.set(true);
        return;
      }

      if (typeof IntersectionObserver === 'undefined') {
        this._hasIntersected.set(true);
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            this._hasIntersected.set(true);
            observer.disconnect();
          }
        },
        {
          rootMargin: this._ioRootMargin(),
          threshold: this._ioThreshold(),
        },
      );
      observer.observe(this._elRef.nativeElement);
      onCleanup(() => observer.disconnect());
    }, { allowSignalWrites: true });
  }

  protected readonly _from = computed(() => this.config().from ?? 0);

  protected readonly _fraction = computed(() => {
    const c = this.config();
    const from = this._from();
    if (c.total <= from) {
      return 0;
    }
    return Math.min(1, Math.max(0, (c.current - from) / (c.total - from)));
  });

  /** Fraction used for visuals; **0** until viewport reveal when `animateOnVisible` is **true**. */
  protected readonly _displayFraction = computed(() => {
    if (!this._animateOnVisible()) {
      return this._fraction();
    }
    return this._hasIntersected() ? this._fraction() : 0;
  });

  /** Bar fill width **%** (unrounded) for linear tracks. */
  protected readonly _fillWidthPercent = computed(() => this._displayFraction() * 100);

  protected readonly _fromLabel = computed(() => this.config().fromLabel ?? 'From');
  protected readonly _currentLabel = computed(() => this.config().currentLabel ?? 'Current');
  protected readonly _totalLabel = computed(() => this.config().totalLabel ?? 'Total');

  protected readonly _segmentItems = computed(() => {
    const labels = this.config().segmentLabels;
    const list = labels?.length ? [...labels] : [''];
    const n = list.length;
    const linearPos = this._displayFraction() * n;
    return list.map((label, i) => ({
      label,
      fillPct: Math.min(100, Math.max(0, (linearPos - i) * 100)),
    }));
  });

  protected readonly _showLinearHeader = computed(() => {
    const c = this.config();
    return !!(c.title || c.headerValue);
  });

  protected readonly _miniValueColor = computed(() => {
    const c = this.config();
    return c.valueLabelColor ?? 'var(--cads-color-primary)';
  });

  /**
   * `aria-labelledby` when `title` is present (via {@link _ariaTitleDomId}) or when the host sets `ariaLabelledBy`.
   * Mutually exclusive with {@link _linearAriaLabel} for a given render.
   */
  protected readonly _linearAriaLabelledBy = computed(() => {
    const c = this.config();
    if (c.ariaLabelledBy) {
      return c.ariaLabelledBy;
    }
    if (c.ariaLabel) {
      return null;
    }
    if (c.title) {
      return this._ariaTitleDomId;
    }
    return null;
  });

  /**
   * Shared `aria-label` when `aria-labelledby` is not used: explicit {@link CadsProgressBarConfig.ariaLabel}, else mini
   * `valueLabel`, else default/segmented `headerValue` when there is no `title`, else **Progress**.
   */
  protected readonly _linearAriaLabel = computed(() => {
    const c = this.config();
    const v = this.variant();
    if (c.ariaLabelledBy) {
      return null;
    }
    if (c.ariaLabel) {
      return c.ariaLabel;
    }
    if (c.title) {
      return null;
    }
    if (v === 'mini' && c.valueLabel) {
      return c.valueLabel;
    }
    if ((v === 'default' || v === 'segmented') && c.headerValue) {
      return c.headerValue;
    }
    return 'Progress';
  });

  protected readonly _ringSize = computed(() =>
    this.variant() === 'circularLarge' ? 100 : 50,
  );

  protected readonly _strokeWidth = computed(() =>
    this.variant() === 'circularLarge' ? 10 : 5,
  );

  protected readonly _showCircularSubtitle = computed(() => this.variant() === 'circularLarge');

  protected readonly _ringRadius = computed(() => {
    const size = this._ringSize();
    const sw = this._strokeWidth();
    return (size - sw) / 2;
  });

  protected readonly _ringCircumference = computed(() => 2 * Math.PI * this._ringRadius());

  protected readonly _ringDashOffset = computed(
    () => this._ringCircumference() * (1 - this._displayFraction()),
  );

  protected readonly _ringCenter = computed(() => this._ringSize() / 2);

  protected readonly _ringTransform = computed(() => {
    const c = this._ringCenter();
    return `rotate(-90 ${c} ${c})`;
  });

  /**
   * Ordered range endpoints for ARIA (`aria-valuemin` / `aria-valuemax`). When `total <= from`, min/max are normalized so
   * `min < max` (duplicate endpoints use `max = min + 1`).
   */
  private readonly _ariaRangeLo = computed(() => {
    const from = this._from();
    const total = this.config().total;
    return Math.min(from, total);
  });

  private readonly _ariaRangeHi = computed(() => {
    const from = this._from();
    const total = this.config().total;
    const lo = Math.min(from, total);
    const hi = Math.max(from, total);
    return hi > lo ? hi : lo + 1;
  });

  /**
   * Matches the visual fill: `lo + fraction * (hi - lo)` with {@link _fraction} (same as bar), so it stays within
   * [`aria-valuemin`, `aria-valuemax`] and aligns with clamped progress.
   */
  protected readonly _ariaValueNow = computed(() => {
    const lo = this._ariaRangeLo();
    const hi = this._ariaRangeHi();
    const span = hi - lo;
    if (span <= 0) {
      return lo;
    }
    return lo + this._fraction() * span;
  });

  protected readonly _ariaValueMin = computed(() => this._ariaRangeLo());

  protected readonly _ariaValueMax = computed(() => this._ariaRangeHi());
}
